import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

const ALL_VALUE = "__all__";

function buildFilters(
  current: FilterConditionDTO[],
  updates: { search?: string; type?: string },
): FilterConditionDTO[] {
  let next = removeConditions(current, "search") ?? [];
  next = removeConditions(next, "type") ?? [];
  if (updates.search) {
    next = upsertCondition(next, {
      field: "search",
      op: "contains",
      value: updates.search,
    });
  }
  if (updates.type) {
    next = upsertCondition(next, {
      field: "type",
      op: "eq",
      value: updates.type,
    });
  }
  return next;
}

export function StockMovementsFilter() {
  const nav = useNavigate({ from: "/app/inventory/movements" });
  const search = useSearch({ from: "/app/inventory/movements" }) as {
    limit?: number;
    offset?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
    filters?: FilterConditionDTO[] | string;
  };

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const searchFilter = findCondition(filters, "search");
  const typeFilter = findCondition(filters, "type");

  const [searchValue, setSearchValue] = useState<string>(
    (searchFilter?.value as string) || "",
  );
  const [typeValue, setTypeValue] = useState<string>(
    (typeFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((searchFilter?.value as string) || ""),
    [searchFilter?.value],
  );
  useEffect(
    () => setTypeValue((typeFilter?.value as string) || ""),
    [typeFilter?.value],
  );

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearchValue(val);
    const next = buildFilters(filters, {
      search: val || undefined,
      type: typeValue || undefined,
    });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  }, 400);

  const applyType = (val: string) => {
    const effective = val === ALL_VALUE ? "" : val;
    setTypeValue(effective);
    const next = buildFilters(filters, {
      search: searchValue || undefined,
      type: effective || undefined,
    });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  };

  const hasFilter = Boolean(searchFilter?.value || typeFilter?.value);

  const typeOptions = [
    { label: "ທຸກປະເພດ", value: ALL_VALUE },
    { label: "ປັບ", value: "adjustment" },
    { label: "ຂາຍ", value: "sale" },
    { label: "ຄືນ", value: "return" },
    { label: "ເຕີມສະຕ໋ອກ", value: "restock" },
  ];

  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 px-2">
      <Input
        placeholder="ຄົ້ນຫາຕາມຊື່ ຫຼື SKU..."
        defaultValue={searchValue}
        onChange={(e) => {
          const v = e.target.value;
          debouncedSearch(v);
        }}
        className="h-8 sm:max-w-2xs"
      />
      <SimpleSelect
        placeholder="ປະເພດ"
        className="h-8 w-[140px]"
        value={typeValue || ALL_VALUE}
        onValueChange={applyType}
        options={typeOptions}
      />
      {hasFilter && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            nav({
              search: {
                ...search,
                offset: 0,
                filters: undefined,
              } as Record<string, unknown>,
            })
          }
        >
          ລ້າງຕົວກອງ
        </Button>
      )}
    </div>
  );
}
